import {
  getSPBlobWithParams,
  getSPDataWithParams,
  getSPDataWithoutParams,
  postSPDataWithoutParams,
  postSPFormDataWithoutParams,
  putSPDataWithParams,
} from './services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const EVENTS_BASE = '/api/secure/cc/support/events'
const ACTIONS_BASE = `${EVENTS_BASE}/actions`

function getResponseMessage(response, fallbackMessage) {
  return (
    response?.data?.message ||
    response?.response?.data?.message ||
    response?.message ||
    fallbackMessage
  )
}

function toActionResult(response, fallbackErrorMessage) {
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response),
      rawResponse: response,
    }
  }
  return {
    ok: false,
    message: getResponseMessage(response, fallbackErrorMessage),
    rawResponse: response,
  }
}

/**
 * Nudges the booking's practitioner to complete a started-but-not-completed session, over the given
 * channels ('whatsapp' and/or 'email'; empty = both). The reminder carries a deep link to open and
 * finish the session; WhatsApp falls back to a Twilio template opener outside the 24-hour window.
 */
export async function sendSupportSessionCompletionReminder(bookedEventId, modes) {
  const response = await postSPDataWithoutParams(`${ACTIONS_BASE}/session-completion-reminder`, {
    bookedEventId,
    modes: Array.isArray(modes) && modes.length ? modes : undefined,
  })
  return toActionResult(response, 'Could not send the completion reminder.')
}

// Report-type-aware clinical-document base (prescription / therapy / screening / assessment report).
// Distinct from ACTIONS_BASE: the send goes out FROM AUMHUM, not on the practitioner's behalf.
const PRACTITIONER_REPORTS_BASE = '/api/secure/cc/support/practitioner/emr-reports'

// --- Patient quick-actions parity --------------------------------------------------------------
// Payment link and bill are performed on the booking practitioner's behalf (backend resolves the
// practitioner from the booking). The clinical DOCUMENT send below is different: AUMHUM delivers it.

/**
 * Sends the session's clinical document to the patient FROM AUMHUM. `reportType` is one of
 * 'prescription' | 'therapy-report' | 'screening-report' | 'assessment-report'. The backend generates
 * the practitioner's report and delivers it over the AUMHUM patient-document channel (WhatsApp + email),
 * attributed to support — never to the practitioner. `providerName` is the clinic-brand title shown to
 * the patient; `channels` optionally restricts delivery (defaults to every channel on file).
 */
export async function sendSupportReportToPatient(reportType, bookedEventId, { channels, providerName } = {}) {
  const response = await postSPDataWithoutParams(`${PRACTITIONER_REPORTS_BASE}/send`, {
    reportType,
    bookedEventId,
    ...(Array.isArray(channels) && channels.length ? { channels } : {}),
    ...(providerName ? { providerName } : {}),
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      documentName: data?.documentName || '',
      channels: data?.channels || '',
      whatsappSent: !!data?.whatsappSent,
      emailSent: !!data?.emailSent,
      rawResponse: response,
    }
  }
  return { ok: false, message: getResponseMessage(response, 'Unable to send the document.'), rawResponse: response }
}

/**
 * Lists the patient's care-team practitioners eligible to receive this report (per the report→target-role
 * rules), each tagged with role(s) and whether they're the pre-step consultation's doctor (isTriggering).
 */
export async function getSupportReportRecipients(reportType, bookedEventId) {
  const response = await getSPDataWithParams(`${PRACTITIONER_REPORTS_BASE}/recipients`, {
    reportType,
    eventId: bookedEventId,
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      recipients: Array.isArray(data?.recipients) ? data.recipients : [],
      targetRoles: Array.isArray(data?.targetRoles) ? data.targetRoles : [],
      rawResponse: response,
    }
  }
  return {
    ok: false,
    recipients: [],
    targetRoles: [],
    message: getResponseMessage(response, 'Unable to load recipients.'),
  }
}

/**
 * Forwards the session's report to a selected care-team practitioner FROM AUMHUM. `deliveryMode` is
 * 'attachment' | 'link'; `channels` optionally restricts to whatsapp/email (defaults to those on file).
 */
export async function sendSupportReportToPractitioner(
  reportType,
  bookedEventId,
  recipientPractitionerId,
  { deliveryMode, channels } = {},
) {
  const response = await postSPDataWithoutParams(`${PRACTITIONER_REPORTS_BASE}/send-to-practitioner`, {
    reportType,
    bookedEventId,
    recipientPractitionerId,
    ...(deliveryMode ? { deliveryMode } : {}),
    ...(Array.isArray(channels) && channels.length ? { channels } : {}),
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      recipientName: data?.recipientName || '',
      documentName: data?.documentName || '',
      channels: data?.channels || '',
      deliveredInline: !!data?.deliveredInline,
      rawResponse: response,
    }
  }
  return { ok: false, message: getResponseMessage(response, 'Unable to send the report.'), rawResponse: response }
}

export async function sendSupportPatientPaymentLink(bookedEventId, amount, purpose = '', fromName = '') {
  const response = await postSPDataWithoutParams(`${ACTIONS_BASE}/send-payment-link`, {
    bookedEventId,
    amount,
    purpose,
    ...(fromName ? { fromName } : {}),
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      paymentLink: data?.paymentLink || '',
      amount: data?.amount,
      warmWindowOpen: !!data?.warmWindowOpen,
      whatsappSent: !!data?.whatsappSent,
      emailSent: !!data?.emailSent,
      rawResponse: response,
    }
  }
  return { ok: false, message: getResponseMessage(response, 'Unable to create the payment link.'), rawResponse: response }
}

export async function createSupportPatientReceipt(bookedEventId, input = {}) {
  const response = await postSPDataWithoutParams(`${ACTIONS_BASE}/bill-patient`, { bookedEventId, ...input })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true, ...(getDataMapFromCommonResponse(response) || {}), rawResponse: response }
  }
  return { ok: false, message: getResponseMessage(response, 'Unable to create the receipt.'), rawResponse: response }
}

export async function fetchSupportPatientReceiptPdfBlobUrl(receiptId) {
  try {
    const response = await getSPBlobWithParams(`${ACTIONS_BASE}/bill-patient/pdf`, { receiptId })
    if (response?.status === 200 && response?.data) {
      return { ok: true, blobUrl: URL.createObjectURL(response.data) }
    }
    return { ok: false, message: 'Unable to load the receipt PDF.' }
  } catch {
    return { ok: false, message: 'Unable to load the receipt PDF.' }
  }
}

export async function sendSupportPatientReceipt(receiptId, channels = []) {
  const response = await postSPDataWithoutParams(`${ACTIONS_BASE}/bill-patient/send`, { receiptId, channels })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true, ...(getDataMapFromCommonResponse(response) || {}), rawResponse: response }
  }
  return { ok: false, message: getResponseMessage(response, 'Unable to send the receipt.'), rawResponse: response }
}

/** Marks the session completed on behalf of the practitioner. */
export async function markSupportBookedEventCompleted(bookedEventId) {
  const response = await putSPDataWithParams(`${EVENTS_BASE}/booked-event/finish`, null, {
    bookedEventId,
  })
  return toActionResult(response, 'Could not mark the session completed.')
}

/**
 * Reassigns the booking to another practitioner (role-aware per event family).
 * payload: { bookedEventId, newMentorId, sendCommunicationToClient, sendCommunicationToMentor, reason }
 */
export async function reassignSupportBookedEvent(payload) {
  const response = await postSPDataWithoutParams(`${ACTIONS_BASE}/reassign`, payload)
  return toActionResult(response, 'Could not reassign the booking.')
}

/**
 * Changes a booking's meeting mode. targetMode is the destination meetingSrc value:
 * 'aumhum' (Video-Call) or 'offline' (In-Clinic).
 * payload: { bookedEventId, targetMode, reason }
 */
export async function changeSupportBookedEventMode(payload) {
  const response = await postSPDataWithoutParams(`${ACTIONS_BASE}/change-mode`, payload)
  return toActionResult(response, 'Could not change the booking mode.')
}

/**
 * Confirms an out-of-band payment on a payment-pending booking.
 * payload: { bookedEventId, reasonGiven, collectedByParty, paymentGatewayUsed, paymentTxnId,
 *            feeCollectionNote, proofDocuments }
 */
export async function markSupportBookedEventAsPaid(payload) {
  const response = await postSPDataWithoutParams(`${ACTIONS_BASE}/mark-as-paid`, {
    platform: 'web',
    ...payload,
  })
  return toActionResult(response, 'Could not mark the booking as paid.')
}

/**
 * Uploads a fee-collection proof (screenshot/receipt). Returns
 * { ok, proofDocument: { fileId, fileName, mimeType } }.
 */
export async function uploadSupportFeeProof(file) {
  const formData = new FormData()
  formData.append('file', file)
  const response = await postSPFormDataWithoutParams(`${ACTIONS_BASE}/fee-proof/upload`, formData)
  const result = toActionResult(response, 'Could not upload the document.')
  if (result.ok) {
    return { ok: true, proofDocument: result.data?.proofDocument || null }
  }
  return result
}

/** Resolves a short-lived presigned URL to view a fee-proof document. Returns { ok, url }. */
export async function getSupportFeeProofUrl(fileId) {
  const response = await getSPDataWithoutParams(`${ACTIONS_BASE}/fee-proof/${fileId}/url`)
  const result = toActionResult(response, 'Could not open the document.')
  if (result.ok) {
    return { ok: true, url: result.data?.url || null }
  }
  return result
}

/** Toggles the test flag on a booking (test bookings are excluded from stats). */
export async function markSupportBookedEventAsTest(bookedEventId, markAsTest) {
  const path = markAsTest ? 'mark-as-test' : 'unmark-as-test'
  const response = await postSPDataWithoutParams(
    `${ACTIONS_BASE}/${path}?${new URLSearchParams({ bookedEventId })}`,
    null,
  )
  return toActionResult(
    response,
    markAsTest ? 'Could not mark the booking as test.' : 'Could not remove the test flag.',
  )
}

/** Overrides the video-call (RTC) provider for an online session. */
export async function updateSupportBookedEventRtcProvider({ bookedEventId, rtcProviderOverride }) {
  const response = await putSPDataWithParams(`${ACTIONS_BASE}/rtc-provider-override`, {
    bookedEventId,
    rtcProviderOverride: rtcProviderOverride || null,
  })
  return toActionResult(response, 'Could not update the video provider.')
}

/** Practitioner snippets for the reassign picker. Returns []. */
export async function searchSupportReassignPractitioners(search, eventType) {
  const response = await getSPDataWithParams(`${ACTIONS_BASE}/practitioners/search`, {
    search: search || undefined,
    // Scopes results to practitioners approved for this clinical event type
    // (consultation -> doctors, therapy-session -> therapists, ...). Ignored
    // by the server until the matching backend deploys.
    'event-type': eventType || undefined,
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return getDataMapFromCommonResponse(response)?.practitioners || []
  }
  return []
}

/**
 * Clinic-scoped reassignment options for a booking. The server applies clinic role,
 * approved-service and clinic-appointment-type inclusion/exclusion rules.
 */
export async function getSupportReassignOptions(bookedEvent) {
  if (!bookedEvent?.clinicBrandId || !bookedEvent?.clinicId || !bookedEvent?.eventType) {
    return { practitioners: [], appointmentType: null, message: 'This booking has no clinic appointment context.' }
  }
  const response = await getSPDataWithParams(
    '/api/secure/cc/support/clinic/clinic-appointment-type/options/practitioners',
    {
      'clinic-brand-id': bookedEvent.clinicBrandId,
      'clinic-location-id': bookedEvent.clinicId,
      eventType: bookedEvent.eventType,
      'appointment-type-id': (bookedEvent.useClinicServices ||
        String(bookedEvent.eventTypeId || '').startsWith('CAP-'))
        ? bookedEvent.eventTypeId
        : undefined,
    },
  )
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      practitioners: data.practitioners || [],
      appointmentType: data.appointmentType || null,
      message: '',
    }
  }
  return {
    practitioners: [],
    appointmentType: null,
    message: getResponseMessage(response, 'Eligible practitioners could not be loaded.'),
  }
}

/** Sends the online-session join link over WhatsApp. kind: 'secure' | 'public'. */
export async function sendSupportBookedEventJoinLink({
  bookedEventId,
  kind = 'secure',
  toCustomMobile,
  toHumanSupport = false,
}) {
  const linkPath = kind === 'public' ? 'public-join-link' : 'secure-join-link'
  const params = new URLSearchParams({ 'booked-event-id': bookedEventId })
  let path
  if (toHumanSupport) {
    path = `${EVENTS_BASE}/${linkPath}/send-to-human-support?${params}`
  } else {
    if (toCustomMobile) {
      params.set('to-custom-mobile', toCustomMobile)
    }
    path = `${EVENTS_BASE}/${linkPath}/send?${params}`
  }
  const response = await postSPDataWithoutParams(path, null)
  return toActionResult(response, 'Could not send the join link.')
}

/** Raw video-session tokens (join links/codes) for the given booked events. Returns []. */
export async function getSupportVideoSessionTokens(bookedEventIds = []) {
  const response = await getSPDataWithParams(`${EVENTS_BASE}/video-session-tokens/get`, {
    'booked-event-ids': (bookedEventIds || []).filter(Boolean).join(','),
  })
  if (response?.status === 200 && Array.isArray(response?.data)) {
    return response.data
  }
  return []
}

/**
 * A page of booked-event logs (session timeline + the support action audit trail).
 * Returns { logs, totalElements }.
 */
export async function getSupportBookedEventLogs(bookedEventId, { page = 0, size = 200 } = {}) {
  const response = await getSPDataWithParams(`${EVENTS_BASE}/logs/get`, {
    'booked-event-id': bookedEventId,
    page,
    size,
    sort: 'createdTime,desc',
  })
  if (response?.status === 200 && Array.isArray(response?.data?.content)) {
    return {
      logs: response.data.content,
      totalElements: response.data.totalElements || response.data.content.length,
    }
  }
  return { logs: [], totalElements: 0 }
}
